import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import axiosInstance from "../services/axiosInstance";
import TokenManager from "../services/tokenManager";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./Client.css";

const TAEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const formRef = useRef();

  // State Management
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");

  // Form data
  const [formData, setFormData] = useState({
    employeeName: "",
    monthYear: "",
    projectName: "",
    costCenter: "",
    designation: "",
    supportingTA: "",
    payment: "",
    location: "",
  });

  // Expense rows
  const [rows, setRows] = useState([]);

  // Load form data on mount
  useEffect(() => {
    if (id) {
      fetchFormData();
    }
  }, [id]);

  const fetchFormData = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/api/ta/submissions/${id}`);
      
      if (response.data.success) {
        const { submission, items } = response.data.data;
        
        // Pre-fill form data
        setFormData({
          employeeName: submission.employee_name || "",
          monthYear: submission.month_year || "",
          projectName: submission.project_name || "",
          costCenter: submission.cost_center || "",
          designation: submission.designation || "",
          supportingTA: submission.supporting_ta || "",
          payment: submission.payment || "",
          location: submission.location || "",
        });

        // Pre-fill expense rows
        const formattedRows = items.map((item) => ({
          id: item.id,
          date: item.item_date || "",
          site: item.site || "",
          costCenter: item.cost_center || "",
          particulars: item.particulars || "",
          mode: item.mode || "Cash",
          amount: item.amount || "",
          ticketImage: null,
        }));
        
        setRows(formattedRows);
      }
    } catch (err) {
      showNotificationMessage("error", "Failed to load form data");
      console.error("Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const showNotificationMessage = (type, message) => {
    setNotificationType(type);
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const addRow = () => {
    const newId = rows.length ? Math.max(...rows.map((r) => r.id || 0)) + 1 : 1;
    setRows([
      ...rows,
      {
        id: newId,
        date: "",
        costCenter: "",
        particulars: "",
        mode: "Cash",
        amount: "",
        ticketImage: null,
      },
    ]);
  };

  const deleteRow = (id) => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const updateRow = (id, field, value) => {
    setRows(
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleTicketImageUpload = (id, file) => {
    if (file) {
      updateRow(id, "ticketImage", file);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.employeeName?.trim()) errors.employeeName = "Employee name is required";
    if (!formData.monthYear?.trim()) errors.monthYear = "Month/Year is required";
    if (!formData.designation?.trim()) errors.designation = "Designation is required";
    if (rows.length === 0) {
      errors.rows = "At least one expense row is required";
    } else {
      rows.forEach((row, index) => {
        if (!row.date?.trim()) errors[`row_${row.id}_date`] = `Row ${index + 1}: Date required`;
        if (!row.amount || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0)
          errors[`row_${row.id}_amount`] = `Row ${index + 1}: Valid amount required`;
      });
    }
    return errors;
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Travel Allowance Form (Draft)", 20, 20);
    doc.setFontSize(10);
    let yPos = 35;

    doc.text(`Name: ${formData.employeeName}`, 20, yPos);
    yPos += 5;
    doc.text(`Month/Year: ${formData.monthYear}`, 20, yPos);
    yPos += 5;
    doc.text(`Designation: ${formData.designation}`, 20, yPos);
    yPos += 10;

    const tableData = rows.map((row) => [
      row.date,
      row.site,
      row.particulars,
      row.mode,
      `₹${parseFloat(row.amount || 0).toFixed(2)}`,
    ]);

    autoTable(doc, {
      head: [["Date", "Site", "Particulars", "Mode", "Amount"]],
      body: tableData,
      startY: yPos,
    });

    doc.save(`TA_Form_${id}.pdf`);
    showNotificationMessage("success", "PDF downloaded successfully");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showNotificationMessage("error", "Please fix the validation errors");
      return;
    }

    const token = TokenManager.getAccessToken();
    if (!token) {
      showNotificationMessage("error", "Session expired. Please login again.");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    try {
      setIsSubmitting(true);

      const formDataToSend = {
        employeeName: formData.employeeName,
        monthYear: formData.monthYear,
        costCenter: formData.costCenter,
        designation: formData.designation,
        supportingTA: formData.supportingTA,
        payment: formData.payment,
        location: formData.location,
        rows: rows.map((row) => ({
          date: row.date,
          site: row.site,
          particulars: row.particulars,
          mode: row.mode,
          amount: parseFloat(row.amount),
        })),
      };

      const response = await axiosInstance.put(
        `/api/ta/submissions/${id}`,
        formDataToSend
      );

      if (response.data.success) {
        showNotificationMessage("success", "Form updated successfully! Redirecting...");
        setTimeout(() => navigate("/ta-list"), 2000);
      }
    } catch (error) {
      showNotificationMessage(
        "error",
        error.response?.data?.message || "Failed to update form"
      );
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Notification Popup */}
      {showNotification && (
        <div
          className={`notification-popup notification-${notificationType}`}
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            padding: "12px 16px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: "280px",
            backgroundColor:
              notificationType === "success" ? "#d4edda" : "#f8d7da",
            color: notificationType === "success" ? "#155724" : "#721c24",
            border: `1px solid ${
              notificationType === "success" ? "#c3e6cb" : "#f5c6cb"
            }`,
          }}
        >
          <i
            className={`bi ${
              notificationType === "success"
                ? "bi-check-circle-fill"
                : "bi-exclamation-triangle-fill"
            }`}
            style={{ fontSize: "1.2rem" }}
          ></i>
          <span style={{ flex: 1 }}>{notificationMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-link text-dark p-0 me-2"
            style={{ fontSize: "1.8rem", lineHeight: 1, textDecoration: "none" }}
            title="Go back"
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <h2 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 500, color: "var(--secondary)" }}>
            Edit Travel Allowance Form
          </h2>
        </div>
        <button
          onClick={downloadPDF}
          className="btn btn-outline-info btn-sm"
        >
          <i className="bi bi-file-pdf me-1"></i>Download PDF
        </button>
      </div>

      <section className="section" style={{ padding: 0 }}>
        <div className="row" style={{ margin: 0 }}>
          <div className="col-lg-12" style={{ padding: 0 }}>
            <div className="card">
              <div className="card-body">
                <form ref={formRef} noValidate onSubmit={handleSubmit}>
                  {/* Employee Details Section */}
                  <h5 className="mb-3">Employee Details</h5>
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <label htmlFor="employeeName" className="form-label">
                        Name <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="employeeName"
                        className={`form-control form-control-sm ${validationErrors.employeeName ? "is-invalid" : ""}`}
                        id="employeeName"
                        value={formData.employeeName}
                        onChange={handleInputChange}
                      />
                      {validationErrors.employeeName && (
                        <div className="invalid-feedback">{validationErrors.employeeName}</div>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="monthYear" className="form-label">
                        Month/Year <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="monthYear"
                        className={`form-control form-control-sm ${validationErrors.monthYear ? "is-invalid" : ""}`}
                        id="monthYear"
                        placeholder="e.g. Jan,2026"
                        value={formData.monthYear}
                        onChange={handleInputChange}
                      />
                      {validationErrors.monthYear && (
                        <div className="invalid-feedback">{validationErrors.monthYear}</div>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="costCenter" className="form-label">
                        Cost Center
                      </label>
                      <input
                        type="text"
                        name="costCenter"
                        className="form-control form-control-sm"
                        id="costCenter"
                        value={formData.costCenter}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="designation" className="form-label">
                        Designation <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="designation"
                        className={`form-control form-control-sm ${validationErrors.designation ? "is-invalid" : ""}`}
                        id="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                      />
                      {validationErrors.designation && (
                        <div className="invalid-feedback">{validationErrors.designation}</div>
                      )}
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <label htmlFor="supportingTA" className="form-label">
                        Supporting TA
                      </label>
                      <input
                        type="text"
                        name="supportingTA"
                        className="form-control form-control-sm"
                        id="supportingTA"
                        value={formData.supportingTA}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="payment" className="form-label">
                        Payment Method
                      </label>
                      <input
                        type="text"
                        name="payment"
                        className="form-control form-control-sm"
                        id="payment"
                        value={formData.payment}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="location" className="form-label">
                        Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        className="form-control form-control-sm"
                        id="location"
                        value={formData.location}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Expense Rows Section */}
                  <div className="mb-4">
                    <h5 className="mb-3">
                      Expense Details{" "}
                      <span style={{ color: "#dc3545" }}>
                        *
                      </span>
                    </h5>
                    {validationErrors.rows && (
                      <div className="alert alert-danger mb-3">
                        {validationErrors.rows}
                      </div>
                    )}

                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "12%" }}>Date *</th>
                            <th style={{ width: "15%" }}>Site</th>
                            <th style={{ width: "20%" }}>Particulars</th>
                            <th style={{ width: "12%" }}>Mode</th>
                            <th style={{ width: "15%" }}>Amount *</th>
                            <th style={{ width: "12%" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, index) => (
                            <tr key={row.id}>
                              <td>
                                <input
                                  type="date"
                                  className={`form-control form-control-sm ${
                                    validationErrors[`row_${row.id}_date`]
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  value={row.date}
                                  onChange={(e) =>
                                    updateRow(row.id, "date", e.target.value)
                                  }
                                />
                                {validationErrors[`row_${row.id}_date`] && (
                                  <div
                                    className="invalid-feedback"
                                    style={{ display: "block" }}
                                  >
                                    {validationErrors[`row_${row.id}_date`]}
                                  </div>
                                )}
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Client office"
                                  value={row.site}
                                  onChange={(e) =>
                                    updateRow(row.id, "site", e.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Travel details"
                                  value={row.particulars}
                                  onChange={(e) =>
                                    updateRow(row.id, "particulars", e.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={row.mode}
                                  onChange={(e) =>
                                    updateRow(row.id, "mode", e.target.value)
                                  }
                                >
                                  <option>Cash</option>
                                  <option>UPI</option>
                                  <option>Card</option>
                                  <option>Check</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className={`form-control form-control-sm ${
                                    validationErrors[`row_${row.id}_amount`]
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder="0.00"
                                  step="0.01"
                                  value={row.amount}
                                  onChange={(e) =>
                                    updateRow(row.id, "amount", e.target.value)
                                  }
                                />
                                {validationErrors[`row_${row.id}_amount`] && (
                                  <div
                                    className="invalid-feedback"
                                    style={{ display: "block" }}
                                  >
                                    {validationErrors[`row_${row.id}_amount`]}
                                  </div>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => deleteRow(row.id)}
                                  className="btn btn-sm btn-danger"
                                  title="Delete row"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      type="button"
                      onClick={addRow}
                      className="btn btn-sm btn-outline-primary mt-2"
                    >
                      <i className="bi bi-plus-lg me-1"></i>Add Row
                    </button>
                  </div>

                  {/* Buttons */}
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-primary"
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg me-1"></i>Update & Resubmit
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TAEdit;
